import { useWereChanged } from '@allvue/common/src/everestStyleComponents/table/helper';
import { IObject } from '@allvue/common/src/helpers/interface';
import Utils from '@allvue/common/src/helpers/utils';
import { IPopupReducer } from '@allvue/common/src/store/popup/popupRedux';
import React, {
  FunctionComponent, useEffect, useMemo, useState
} from 'react';
import { useSelector } from 'react-redux';
import styles from './PopupPortalOverlay.module.scss';

enum MessageType {
  popupsAreOpened = 'fund-subscription_popups-are-opened',
  popupsAreClosed = 'fund-subscription_popups-are-closed',
  moveContent = 'fund-subscription_move-content'
}

interface IPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

const PopupPortalOverlay: FunctionComponent = ({ children }) => {
  const [contentPosition, setContentPosition] = useState<IPosition>();

  const numberOfOpenedPopups = useSelector((store: IObject<IPopupReducer<unknown>>) => {
    return Object.entries(store.popups ?? {}).reduce((res, [_, val]) => {
      return res + Number(val.open);
    }, 0);
  });

  const [isOverlayApplied, setOverlayApplied] = useState(false);

  const postMessage = (type: MessageType, data: IObject = {}) => {
    window.parent.postMessage({ type, data }, '*');
  };

  useEffect(() => {
    if (Utils.isInIframe()) {
      if (numberOfOpenedPopups > 0) {
        setOverlayApplied(true);
      } else {
        setOverlayApplied(false);
      }
    }
  }, [numberOfOpenedPopups]);

  useEffect(() => {
    if (isOverlayApplied) {
      postMessage(MessageType.popupsAreOpened);
    } else {
      postMessage(MessageType.popupsAreClosed);
      setContentPosition(undefined);
    }
  }, [useWereChanged(isOverlayApplied)]);

  useEffect(() => {
    const listener = (event: MessageEvent<{ type: MessageType, data: IObject }>) => {
      if (event.origin.endsWith('.verticetech.com') || event.origin.endsWith('.altareturn.com')) {
        try {
          const { type, data } = event.data;

          if (type === MessageType.moveContent) {
            setContentPosition(data as { x: number, y: number, width: number, height: number });
          }
        } catch (e: unknown) {
          // eslint-disable-next-line no-console
          console.log(e);
        }
      }
    };

    window.addEventListener('message', listener);
    return () => window.addEventListener('message', listener);
  }, []);

  const overlayContentStyles = useMemo(() => {
    if (!contentPosition) {
      return {};
    }

    const toPx = (num: number) => `${num}px`;
    const {
      x, y, width, height
    } = contentPosition;
    return {
      top: toPx(y),
      left: toPx(x),
      width: toPx(width),
      height: toPx(height)
    };
  }, [contentPosition]);

  return (
    <div
      className={styles.wrapper}
      style={overlayContentStyles}
    >
      { children }
    </div>
  );
};

export default PopupPortalOverlay;
